import React, { useState } from "react";
import "./Step1OTP.css";

const API_URL = "https://abnbtrust.org/api";

function Step1OTP({
  nextStep,
  formData = {},
  updateFormData,
}) {
  // ==========================================================
  // MOBILE NUMBER
  // ==========================================================

  const [mobileNumber, setMobileNumber] = useState(
    formData.mobileNumber || ""
  );

  // ==========================================================
  // OTP
  // ==========================================================

  const [otp, setOtp] = useState("");

  // ==========================================================
  // OTP SENT
  // ==========================================================

  const [otpSent, setOtpSent] = useState(false);

  // ==========================================================
  // VERIFIED
  // ==========================================================

  const [verified, setVerified] = useState(false);

  // ==========================================================
  // TERMS
  // ==========================================================

  const [agreed, setAgreed] = useState(false);

  // ==========================================================
  // ERROR
  // ==========================================================

  const [error, setError] = useState("");

  // ==========================================================
  // SUCCESS
  // ==========================================================

  const [successMessage, setSuccessMessage] = useState("");

  // ==========================================================
  // LOADING
  // ==========================================================

  const [loading, setLoading] = useState(false);

  // ==========================================================
  // MOBILE NUMBER
  // ==========================================================

  const handleMobileChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");

    if (value.length <= 10) {
      setMobileNumber(value);
      setError("");
      setSuccessMessage("");

      // If user changes mobile number after OTP was sent,
      // reset OTP verification state.
      if (otpSent) {
        setOtpSent(false);
        setOtp("");
        setVerified(false);
      }
    }
  };

  // ==========================================================
  // SEND OTP
  // ==========================================================

  const handleSendOTP = async (event) => {
    event.preventDefault();

    setError("");
    setSuccessMessage("");

    // Validate mobile number
    if (!/^[6-9]\d{9}$/.test(mobileNumber)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    // Validate terms
    if (!agreed) {
      setError(
        "Please agree to the Terms & Conditions and Privacy Policy."
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/send-otp/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mobile: mobileNumber,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Failed to send OTP.");
        return;
      }

      // Save mobile number
      if (typeof updateFormData === "function") {
        updateFormData({
          mobileNumber: mobileNumber,
        });
      }

      // Show OTP input
      setOtpSent(true);

      // Clear old OTP
      setOtp("");

      setSuccessMessage(
        "OTP sent successfully to your mobile number."
      );
    } catch (error) {
      console.error("Send OTP error:", error);

      setError(
        "Unable to connect to the server. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // VERIFY OTP
  // ==========================================================

  const verifyOTP = async (enteredOtp) => {
    setError("");

    if (enteredOtp.length !== 6) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/verify-otp/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mobile: mobileNumber,
          otp: enteredOtp,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Invalid OTP.");
        return;
      }

      // OTP verified successfully
      setVerified(true);

      setSuccessMessage(
        "Mobile number verified successfully."
      );

      // Save verification status
      if (typeof updateFormData === "function") {
        updateFormData({
          mobileNumber: mobileNumber,
          mobileVerified: true,
        });
      }

      // Move to Step 2
      setTimeout(() => {
        if (typeof nextStep === "function") {
          nextStep({
            mobileNumber: mobileNumber,
            mobileVerified: true,
          });
        }
      }, 500);
    } catch (error) {
      console.error("Verify OTP error:", error);

      setError(
        "Unable to verify OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // OTP CHANGE
  // ==========================================================

  const handleOTPChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");

    if (value.length <= 6) {
      setOtp(value);
      setError("");

      // Automatically verify after 6 digits
      if (
        value.length === 6 &&
        otpSent &&
        !verified &&
        !loading
      ) {
        verifyOTP(value);
      }
    }
  };

  // ==========================================================
  // RESEND OTP
  // ==========================================================

  const handleResendOTP = async () => {
    if (!mobileNumber) {
      setError("Mobile number is required.");
      return;
    }

    setOtp("");
    setVerified(false);
    setError("");
    setSuccessMessage("");

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/send-otp/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mobile: mobileNumber,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Failed to resend OTP.");
        return;
      }

      setOtpSent(true);

      setSuccessMessage(
        "A new OTP has been sent successfully."
      );
    } catch (error) {
      console.error("Resend OTP error:", error);

      setError(
        "Unable to connect to the server. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="registration-page">

      <div className="registration-container">

        <div className="compact-step-card">

          {/* ==================================================
              HEADER
          ================================================== */}

          <div className="compact-step-header">
          </div>

          {/* ==================================================
              FORM
          ================================================== */}

          <form onSubmit={handleSendOTP}>

            {/* ==================================================
                MOBILE NUMBER
            ================================================== */}

            <div className="compact-form-group">

              <label>
                Mobile Number <span>*</span>
              </label>

              <div
                className="compact-input-with-button"
                style={{
                  gridTemplateColumns: "70px 1fr",
                }}
              >

                <div
                  style={{
                    minHeight: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #d9dfeb",
                    borderRadius: "6px",
                    background: "#f8f9fc",
                    color: "#17213f",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  +91
                </div>

                <input
                  type="tel"
                  name="mobile"
                  value={mobileNumber}
                  onChange={handleMobileChange}
                  placeholder="Enter mobile number"
                  maxLength="10"
                  autoComplete="off"
                  disabled={verified || loading}
                />

              </div>

            </div>

            {/* ==================================================
                OTP
            ================================================== */}

            <div
              className="compact-form-group"
              style={{ marginTop: "18px" }}
            >

              <label>
                Enter OTP <span>*</span>
              </label>

              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={handleOTPChange}
                placeholder="Enter 6-digit OTP"
                maxLength="6"
                autoComplete="one-time-code"
                disabled={!otpSent || verified || loading}
              />

            </div>

            {/* ==================================================
                SEND OTP
            ================================================== */}

            {!otpSent && (

              <div
                className="compact-buttons"
                style={{
                  gridTemplateColumns: "1fr",
                  marginTop: "20px",
                }}
              >

                <button
                  type="submit"
                  className="compact-button next"
                  disabled={loading}
                >
                  {loading ? "SENDING..." : "SEND OTP"}
                </button>

              </div>

            )}

            {/* ==================================================
                RESEND OTP
            ================================================== */}

            {otpSent && !verified && (

              <button
                type="button"
                onClick={handleResendOTP}
                disabled={loading}
                style={{
                  width: "100%",
                  height: "45px",
                  marginTop: "16px",
                  border: "1px solid #5557cc",
                  borderRadius: "7px",
                  background: "#ffffff",
                  color: "#5557cc",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: loading
                    ? "not-allowed"
                    : "pointer",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? "SENDING..." : "RESEND OTP"}
              </button>

            )}

            {/* ==================================================
                ERROR
            ================================================== */}

            {error && (

              <div
                className="compact-error"
                style={{
                  marginTop: "14px",
                  padding: "10px 12px",
                  background: "#fff5f5",
                  border: "1px solid #fecdca",
                  borderRadius: "6px",
                }}
              >
                {error}
              </div>

            )}

            {/* ==================================================
                SUCCESS
            ================================================== */}

            {successMessage && (

              <div
                style={{
                  marginTop: "14px",
                  padding: "10px 12px",
                  background: "#f0fdf4",
                  border: "1px solid #b7ebc6",
                  borderRadius: "6px",
                  color: "#267a3d",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                ✓ {successMessage}
              </div>

            )}

            {/* ==================================================
                TERMS
            ================================================== */}

            {!verified && (

              <div className="confirm-row">

                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(event) =>
                    setAgreed(event.target.checked)
                  }
                  disabled={loading}
                />

                <span>
                  I agree to the{" "}

                  <span
                    style={{
                      color: "#3153c7",
                      fontWeight: "600",
                    }}
                  >
                    Terms &amp; Conditions
                  </span>

                  {" "}and{" "}

                  <span
                    style={{
                      color: "#3153c7",
                      fontWeight: "600",
                    }}
                  >
                    Privacy Policy
                  </span>

                  .
                </span>

              </div>

            )}

            {/* ==================================================
                SECURITY
            ================================================== */}

          </form>

        </div>

      </div>

    </div>
  );
}

export default Step1OTP;